import { useMemo } from "react"
import { useNavigate } from "@tanstack/react-router"

import type { HelpCenterArticle, HelpCenterGroup } from "@productlane/zero"
import { mutators } from "@productlane/zero/mutators"

import type { ArticleKind } from "./article-kind"
import type { ArticleVisibility } from "./article-visibility"
import { generateId } from "@/lib/id"
import { useZero } from "@/lib/zero"
import { getActivePortalInstanceId } from "@/stores/active-portal-instance-store"
import { DOCS_SECTIONS, useDocNavStore } from "@/stores/docs/docs-nav-store"
import { LINK_ARTICLE_ICON } from "./article-kind"
import { getDefaultArticleIcon } from "./article-visibility"
import { collectHiddenFolders, hiddenFolderKey } from "./hidden-tier-folders"
import { toPublicTreeGroups } from "./public-tree-groups"
import { planTopLevelPlacement } from "./top-level-placement"
import { groupTabId, useDocsTabs } from "./use-docs-tabs"

type ArticleGroups = readonly (HelpCenterGroup & {
	helpCenterArticles: readonly HelpCenterArticle[]
})[]

export function useCreateArticle({
	articleGroups,
	activeArticleId,
}: {
	articleGroups: ArticleGroups
	activeArticleId?: string | null
}) {
	const zero = useZero()
	const navigate = useNavigate()
	const docsNavStore = useDocNavStore()

	const activeGroup = useMemo(
		() =>
			activeArticleId
				? articleGroups.find((g) =>
						g.helpCenterArticles.some((a) => a.id === activeArticleId),
					)
				: undefined,
		[activeArticleId, articleGroups],
	)

	// A new public article belongs to the tab the nav is showing.
	const { tabs, activeTabId } = useDocsTabs()
	const lastGroup = useMemo(
		() =>
			articleGroups
				.filter((group) => groupTabId(group, tabs) === activeTabId)
				.reduce(
					(highest, group) =>
						group.order > (highest?.order ?? -Infinity) ? group : highest,
					null as ArticleGroups[number] | null,
				),
		[articleGroups, tabs, activeTabId],
	)

	// A top-level page joins the run of pages at the bottom of the tab's tree, or
	// opens a new one there.
	const topLevelGroupId = (): string => {
		const tabGroups = toPublicTreeGroups(articleGroups).filter(
			(group) => groupTabId(group, tabs) === activeTabId,
		)
		const placement = planTopLevelPlacement(tabGroups, tabGroups.length)
		if (placement.kind === "existing") return placement.groupId
		const id = generateId()
		zero.mutate(
			mutators.helpCenterGroups.create({
				id,
				name: "",
				order: placement.order,
				portalInstanceId: getActivePortalInstanceId(),
				tabId: activeTabId,
			}),
		)
		return id
	}

	return ({
		visibility = "PUBLIC",
		kind = "DOC",
		groupId,
		topLevel = false,
	}: {
		visibility?: ArticleVisibility
		kind?: ArticleKind
		// The folder rows in the Agent and Internal sections each write into their
		// own group, rather than wherever a section-level button would land.
		groupId?: string
		// A public page outside every group, at the bottom of the active tab.
		topLevel?: boolean
	} = {}) => {
		if (topLevel && kind === "DOC" && visibility === "PUBLIC") {
			const targetGroupId = topLevelGroupId()
			const target = articleGroups.find((g) => g.id === targetGroupId)
			const lastOrder = Math.max(
				-1,
				...(target?.helpCenterArticles ?? []).map((a) => a.order),
			)
			const id = generateId()
			zero.mutate(
				mutators.helpCenterArticles.create({
					id,
					title: "",
					helpCenterGroupId: targetGroupId,
					parentArticleId: null,
					order: lastOrder + 1,
					archived: false,
					content: {},
					date: Date.now(),
					kind: "DOC",
					externalUrl: null,
					icon: getDefaultArticleIcon("PUBLIC"),
					imageBlurhash: null,
					imageUrl: "",
					published: false,
					visibility: "PUBLIC",
					showOnHomePage: false,
					summary: "",
					urlName: "",
					portalInstanceId: getActivePortalInstanceId(),
				}),
			)
			void navigate({ to: "/edit-docs/$id", params: { id } })
			return
		}
		// No group yet, so send the user through group creation first.
		if (articleGroups.length === 0) {
			docsNavStore.setIsNavOpen(true)
			setTimeout(() => {
				docsNavStore.setIsAddGroupOpen(true)
			}, 200)
			return
		}
		// A link renders nowhere customer-facing, so "public" would be a label with
		// no meaning behind it. It is agent knowledge, same as an agent article.
		const rowVisibility = kind === "LINK" ? "UNLISTED" : visibility

		// A hidden row lands in a folder that already holds its tier, so the
		// section-level button never opens a folder out of nowhere.
		const tierGroup =
			rowVisibility === "PUBLIC"
				? undefined
				: collectHiddenFolders(articleGroups, rowVisibility)[0]?.group

		const targetGroup =
			(groupId ? articleGroups.find((g) => g.id === groupId) : undefined) ??
			activeGroup ??
			tierGroup ??
			lastGroup
		// The active tab has no group yet, so create one there first.
		if (!targetGroup) {
			docsNavStore.setIsNavOpen(true)
			setTimeout(() => {
				docsNavStore.setIsAddGroupOpen(true)
			}, 200)
			return
		}

		const lastArticle = targetGroup.helpCenterArticles.reduce(
			(highest, article) =>
				article.order > (highest?.order ?? -Infinity) ? article : highest,
			null as HelpCenterArticle | null,
		)

		const id = generateId()
		zero.mutate(
			mutators.helpCenterArticles.create({
				id,
				title: "",
				helpCenterGroupId: targetGroup.id,
				parentArticleId: null,
				order: lastArticle?.order != null ? lastArticle.order + 1 : 0,
				archived: false,
				content: {},
				date: Date.now(),
				kind,
				externalUrl: null,
				icon:
					kind === "LINK"
						? LINK_ARTICLE_ICON
						: getDefaultArticleIcon(rowVisibility),
				imageBlurhash: null,
				imageUrl: "",
				// The mutator forces this for links anyway; passed for local consistency.
				published: kind === "LINK",
				visibility: rowVisibility,
				showOnHomePage: false,
				summary: "",
				urlName: "",
				portalInstanceId: getActivePortalInstanceId(),
			}),
		)

		// A hidden section carries its own open state per folder, so the public
		// group of the same name stays as the user left it.
		if (rowVisibility === "INTERNAL" || rowVisibility === "UNLISTED") {
			docsNavStore.openSection(
				rowVisibility === "INTERNAL"
					? DOCS_SECTIONS.internal
					: DOCS_SECTIONS.agent,
			)
			docsNavStore.openGroup(hiddenFolderKey(rowVisibility, targetGroup.id))
		} else {
			docsNavStore.openGroup(targetGroup.id)
		}
		void navigate({ to: "/edit-docs/$id", params: { id } })
	}
}
